import Phaser from 'phaser'

import animalTypes from '../data/animals.js'
import mutations from '../data/mutations.js'
import animalMutations from '../data/animalMutations.js'
import allRebirths from '../data/rebirths.js'
import { SfxSynth } from './sfx.js'

const IS_POKI = import.meta.env.VITE_BUILD === 'poki'
const IS_CRAZY = import.meta.env.VITE_BUILD === 'crazy'
const STORAGE_KEY = 'pvz_save_v1'

const safeParse = (s) => {
  try {
    return JSON.parse(s)
  } catch {
    return null
  }
}

// Debounce helper for autosave
function debounce(fn, ms = 800) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), ms)
  }
}

function humanizeNumber(num) {
  if (isNaN(num)) return '0'

  if (num >= 1_000_000_000_000) {
    return (num / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '') + 'T'
  }
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  }
  return num.toLocaleString()
}

function hexToPhaser(hex) {
  return parseInt(hex.replace('#', ''), 16)
}

const rarityOrder = [
  'Common',
  'Uncommon',
  'Rare',
  'Epic',
  'Legendary',
  'Mythic',
  'Godly',
  'Secret',
]

const rarityRank = Object.fromEntries(rarityOrder.map((r, i) => [r, i]))

const slotUnlockPrices = [
  100, 1500, 5000, 10000, 50000, 100000, 250000, 500000, 1000000, 2000000,
  5000000, 10000000, 15000000, 25000000, 50000000, 75000000, 100000000,
  150000000, 200000000, 250000000, 300000000, 500000000,
]
function getSlotPrice(
  slotIndex,
  basePrice = 100,
  growthRate = 1.8,
  exponent = 1.5
) {
  return slotUnlockPrices[slotIndex - 1]
  // const price =
  //   basePrice * Math.pow(growthRate, Math.pow(slotIndex - 1, exponent))
  // return Math.round(price)
}
const AD_REFRESH = 2 * 60 * 1000 // 60s

const CONSTANTS = {
  SIDEWALK_WIDTH: 400,
  LAWN_BORDER: 6,
  HUD_HEIGHT: 90,
  SCROLL_PADDING: 20,
}

export default class GameScene extends Phaser.Scene {
  constructor() {
    super('GameScene')
  }

  init() {
    this.isPaused = false

    // core state
    this.money = 400
    // this.money = 200000
    this.moneyMultiplier = 1
    this.seeds = []
    this.inventory = [] // Max 250
    this.inventoryMax = 250
    this.selectedInventoryIndex = -1
    this.lanes = [2]
    this.slots = 1
    this.rebirths = 0
    this.luck = 1 // Global luck multiplier (1 = normal, higher = better)
    this.weaponDamage = 20 // Damage dealt when clicking enemies
    this.shop = [
      {
        name: 'Cactus',
        icon: '🌵',
        price: 200,
        rarity: 'rare',
        damage: 10,
        cooldown: 1,
      },
      {
        name: 'Strawberry',
        icon: '🍓',
        price: 1250,
        rarity: 'rare',
        damage: 25,
        cooldown: 1,
      },
      {
        name: 'Pumpkin',
        icon: '🎃',
        price: 5000,
        rarity: 'epic',
        damage: 55,
        cooldown: 1,
      },
      {
        name: 'Sunflower',
        icon: '🌻',
        price: 25000,
        rarity: 'epic',
        damage: 115,
        cooldown: 1,
      },
      {
        name: 'Dragon Fruit',
        icon: '🌺',
        price: 100000,
        rarity: 'legendary',
        damage: 250,
        cooldown: 1,
      },
      {
        name: 'Eggplant',
        icon: '🍆',
        price: 250000,
        rarity: 'legendary',
        damage: 500,
        cooldown: 1,
      },
      {
        name: 'Watermelon',
        icon: '🍉',
        price: 1000000,
        rarity: 'mythic',
        damage: 1500,
        cooldown: 2.5,
      },
      {
        name: 'Grape',
        icon: '🍇',
        price: 2500000,
        rarity: 'mythic',
        damage: 1750,
        cooldown: 1,
      },
      {
        name: 'Cocotank',
        icon: '🥥',
        price: 5000000,
        rarity: 'godly',
        damage: 1250,
        cooldown: 1,
      },
      {
        name: 'Carnivorous Plant',
        icon: '🌱',
        price: 25000000,
        rarity: 'godly',
        damage: 2250,
        cooldown: 1,
      },
      {
        name: 'Mr. Carrot',
        icon: '🥕',
        price: 50000000,
        rarity: 'secret',
        damage: 3500,
        cooldown: 1,
      },
      {
        name: 'Tomatrio',
        icon: '🍅',
        price: 125000000,
        rarity: 'secret',
        damage: 9000,
        cooldown: 2,
      },
      {
        name: 'Shroombino',
        icon: '🍄‍🟫',
        price: 200000000,
        rarity: 'secret',
        damage: 12500,
        cooldown: 2,
      },
    ]

    // grids
    this.plantGridSize = { rows: 5, cols: 5 }
    this.animalGridSize = { rows: 5, cols: 2 }

    // types
    this.unitSize = 64
    this.animalTypes = animalTypes

    // containers
    this.plants = []
    this.farmAnimals = []
    this.enemies = null
    this.projectiles = null
    this.shovel = false
    this.weapon = false // Weapon tool for clicking enemies
    this.weaponSprite = null // Persistent weapon sprite that follows cursor
    this.topMoney = 0
    this.indexUnlock = {}

    // timings
    this.shootInterval = 1000
    this.spawnMin = 5000
    this.spawnMax = 12000

    this.ui = {
      moneyEl: null,
      buyBtn: null,
      shop: null,
      shopList: null,
      inventory: null,
      inventoryListEl: null,
    }

    // Tutorial
    this.tutorialFinished = false
    this.tutorialStep = 0

    // Ad refresh timer
    this.adRefreshTimer = 0

    // Sonud
    this.sfx = new SfxSynth()

    // Camera panning
    this.isDragging = false
    this.dragStartX = 0
    this.dragStartY = 0
    this.dragThreshold = 5 // pixels
  }

  // ---------- SAVE / LOAD ----------
  saveGame() {
    try {
      // Validate arrays before serializing - use empty arrays if corrupted
      const safeSeeds = Array.isArray(this.seeds) ? this.seeds : []
      const safeInventory = Array.isArray(this.inventory) ? this.inventory : []
      const safePlants = Array.isArray(this.plants) ? this.plants : []
      const safeFarmAnimals = Array.isArray(this.farmAnimals)
        ? this.farmAnimals
        : []
      const safeLanes = Array.isArray(this.lanes) ? this.lanes : [2]

      // Serialize minimal data (NO Phaser objects)
      const state = {
        v: 1,
        money: Number(this.money) || 0,
        lanes: [...safeLanes],
        slots: Number(this.slots) || 1,
        rebirths: Number(this.rebirths) || 0,
        luck: Number(this.luck) || 1,
        weaponDamage: Number(this.weaponDamage) || 20,
        animalGridSize: this.animalGridSize || { rows: 5, cols: 2 },

        // Save seeds by name (map back from the shop on load)
        seeds: safeSeeds.filter((s) => s && s.name).map((s) => s.name),

        // Inventory is simple data already
        inventory: safeInventory
          .filter((a) => a && a.type)
          .map((a) => ({
            type: a.type,
            weight: Number(a.weight) || 0,
            value: Number(a.value) || 0,
            mutation: a.mutation || 'Normal',
          })),
        indexUnlock: this.indexUnlock || {},

        // Plants: just enough to rebuild visuals
        plants: safePlants
          .filter((p) => p && p.icon !== undefined)
          .map((p) => ({
            row: Number(p.row) || 0,
            col: Number(p.col) || 0,
            icon: p.icon,
            attackPower: Number(p.attackPower) || 0,
            mutation: p.mutation || 'Normal',
          })),

        // Farm animals to rebuild
        farmAnimals: safeFarmAnimals
          .filter((a) => a && a.type)
          .map((a) => ({
            row: Number(a.row) || 0,
            col: Number(a.col) || 0,
            type: a.type,
            weight: Number(a.weight) || 0,
            value: Number(a.value) || 0,
            mutation: a.mutation || 'Normal',
          })),

        tutorialFinished: this.tutorialFinished,
        savedAt: Date.now(),
      }

      // Validate the state can be serialized before saving
      const serialized = JSON.stringify(state)
      if (!serialized || serialized === '{}') {
        console.error('Save serialization produced empty result, aborting save')
        return
      }

      // Create a rotating backup before overwriting (keep last 3)
      try {
        const existingSave = localStorage.getItem(STORAGE_KEY)
        if (existingSave) {
          // Rotate backups: 2 -> 3, 1 -> 2, current -> 1
          const backup2 = localStorage.getItem(`${STORAGE_KEY}_backup_2`)
          if (backup2) {
            localStorage.setItem(`${STORAGE_KEY}_backup_3`, backup2)
          }
          const backup1 = localStorage.getItem(`${STORAGE_KEY}_backup_1`)
          if (backup1) {
            localStorage.setItem(`${STORAGE_KEY}_backup_2`, backup1)
          }
          localStorage.setItem(`${STORAGE_KEY}_backup_1`, existingSave)
        }
      } catch (backupErr) {
        // Backup failed, but continue with save - storage might be full
        console.warn('Failed to create backup:', backupErr)
      }

      localStorage.setItem(STORAGE_KEY, serialized)
    } catch (e) {
      console.error('Failed to save game:', e)
      // Don't write corrupted data - leave existing save intact
    }
  }

  loadGame() {
    // Try main save first, then backups in order
    const saveKeys = [
      STORAGE_KEY,
      `${STORAGE_KEY}_backup_1`,
      `${STORAGE_KEY}_backup_2`,
      `${STORAGE_KEY}_backup_3`,
    ]

    let state = null
    let loadedFromBackup = false

    for (const key of saveKeys) {
      const raw = localStorage.getItem(key)
      if (!raw) continue

      const parsed = safeParse(raw)
      if (parsed && parsed.v === 1) {
        state = parsed
        loadedFromBackup = key !== STORAGE_KEY
        if (loadedFromBackup) {
          console.log(`Recovered save from backup: ${key}`)
        }
        break
      } else if (key === STORAGE_KEY) {
        console.warn('Main save file corrupted, trying backups...')
      }
    }

    if (!state) {
      console.warn('No valid save found')
      return false
    }

    // Rebirth Debug
    // state.money = 10000000
    // state.inventory = [
    //   { type: 'Alessio' },
    //   { type: 'Bambini Crostini' },
    // ]

    try {
      // Apply primitives/arrays with defensive defaults
      this.money = Number(state.money) || this.money
      this.topMoney = Math.max(this.topMoney || 0, this.money)
      this.lanes =
        Array.isArray(state.lanes) && state.lanes.length > 0
          ? state.lanes.slice()
          : this.lanes
      this.slots = Number(state.slots) || this.slots
      this.indexUnlock = state.indexUnlock || {}
      this.rebirths = Number(state.rebirths) || this.rebirths
      this.luck = Number(state.luck) || 1
      this.weaponDamage = Number(state.weaponDamage) || 10
      this.animalGridSize = state.animalGridSize || { rows: 5, cols: 2 }

      this.tutorialFinished = state.tutorialFinished

      // Seeds by name -> map back to shop entries (skip missing)
      if (Array.isArray(state.seeds)) {
        this.seeds = state.seeds
          .filter((name) => name)
          .map((name) => this.shop.find((s) => s.name === name))
          .filter(Boolean)
      }

      // Replace inventory with validation
      if (Array.isArray(state.inventory)) {
        this.inventory = state.inventory
          .filter((a) => a && a.type)
          .map((a) => {
            const indexKey = `${a.type}_${a.mutation || 'Normal'}`
            if (this.indexUnlock[indexKey] === undefined)
              this.indexUnlock[indexKey] = true
            return {
              type: a.type,
              weight: Number(a.weight) || 0,
              value: Number(a.value) || 0,
              mutation: a.mutation || 'Normal',
            }
          })
      }

      // Recalculate plant grid X now that animal grid size is known
      this.plantGridX =
        this.animalGridX + this.animalGridSize.cols * this.animalCell + 12

      // Rebuild visuals (after layout/background exists)
      this._clearPlants()
      if (Array.isArray(state.plants)) {
        state.plants
          .filter((p) => p && p.icon !== undefined)
          .forEach((p) => this._rebuildPlant(p))
      }

      this._clearFarm()
      if (Array.isArray(state.farmAnimals)) {
        state.farmAnimals
          .filter((a) => a && a.type)
          .forEach((a) => this._rebuildFarmAnimal(a))
      }

      // Redraw ground to reflect lanes/slots; sync UI
      this._redrawGround()
      this._syncUI()
      this.refreshInventoryHTML?.()

      // If we recovered from a backup, immediately save to main slot
      if (loadedFromBackup) {
        console.log('Saving recovered state to main save slot')
        this.saveGame()
      }

      return true
    } catch (e) {
      console.error('Error applying loaded save state:', e)
      return false
    }
  }

  clearSave() {
    localStorage.removeItem(STORAGE_KEY)
  }

  // Debounced autosave
  _scheduleSave = debounce(() => this.saveGame(), 800)

  // ---- Cleanup helpers
  _clearPlants() {
    this.plants.forEach((p) => {
      p.text?.destroy()
      p.dmgLabel?.destroy()
    })
    this.plants = []
  }

  _clearFarm() {
    this.farmAnimals.forEach((a) => {
      a.text?.destroy()
    })
    this.farmAnimals = []
  }

  // ---- Rebuilders (do NOT charge money/consume seeds)
  _rebuildPlant(data) {
    const { row, col, icon, attackPower, mutation } = data

    // TODO DO NOT USE ICON FOR IDENTIFIER
    const plantInfo = this.shop.find((s) => s.icon === icon)
    const indexKey = `${plantInfo.name}_${mutation || 'Normal'}`
    if (this.indexUnlock[indexKey] === undefined)
      this.indexUnlock[indexKey] = true

    const x = this.plantGridX + col * this.plantCell + this.plantCell / 2
    const y = this.plantGridY + row * this.plantCell + this.plantCell / 2

    const text = this.add
      .text(x, y, icon, {
        font: `${this.unitSize}px Arial`,
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(5)
    text.setStroke('#000', 3)

    // Restore mutation color and label
    const mutationInfo =
      mutations.find((m) => m.name === mutation) || mutations[0]
    if (mutationInfo.color) text.setTint(hexToPhaser(mutationInfo.color))

    const labelColor = mutationInfo.color || 'black'
    const labelText = `DMG ${humanizeNumber(attackPower)}`

    const dmgLabel = this.add
      .text(x, y + this.plantCell / 2 - 14, labelText, {
        font: 'bold 12px Arial',
        color: labelColor,
      })
      .setOrigin(0.5, 0)
      .setDepth(5)
    dmgLabel.setStroke('#ffffff', 3).setShadow(0, 1, '#00000055', 0)

    this.plants.push({
      row,
      col,
      icon,
      text,
      dmgLabel,
      baseY: y,
      phase: Math.random() * Math.PI * 2,
      lastShot: 0,
      attackPower,
      mutation,
    })
  }

  _rebuildFarmAnimal(a) {
    const { row, col, type, mutation, weight = 0, value = 0 } = a
    const indexKey = `${type}_${mutation || 'Normal'}`
    if (this.indexUnlock[indexKey] === undefined)
      this.indexUnlock[indexKey] = true

    // Respect slots limit
    if (row * this.animalGridSize.cols + col >= this.slots) return

    const x = this.animalGridX + col * this.animalCell + this.animalCell / 2
    const y = this.animalGridY + row * this.animalCell + this.animalCell / 2

    const t = this.add
      .image(x, y, `animal_${type}`)
      .setOrigin(0.5)
      .setDisplaySize(this.unitSize * 1.5, this.unitSize * 1.5)
      .setDepth(1)
    const mutationInfo =
      animalMutations.find((m) => m.name === mutation) || animalMutations[0]
    if (mutationInfo.color) t.setTint(hexToPhaser(mutationInfo.color))

    this.farmAnimals.push({
      row,
      col,
      text: t,
      baseY: y,
      type,
      weight,
      value,
      phase: Math.random() * Math.PI * 2,
      mutation,
    })
  }

  preload() {
    const g = this.make.graphics({ x: 0, y: 0, add: false })
    g.fillStyle(0xffffff, 1)
    g.fillCircle(6, 6, 6)
    g.generateTexture('proj', 12, 12)
    g.destroy()

    // 1px dot texture for particles
    const d = this.make.graphics({ x: 0, y: 0, add: false })
    d.fillStyle(0xffffff, 1)
    d.fillCircle(4, 4, 4)
    d.generateTexture('_dot', 8, 8)
    d.destroy()

    Object.keys(animalTypes).forEach((type) => {
      const textureKey = `animal_${type}`
      this.load.image(textureKey, `./assets/animals/${type}.png`)
    })

    this.load.image('bat', './assets/bat.png')

    this.load.audio('bgm', ['./assets/music.mp3'])
  }

  create() {
    // Fixed cell sizes in world space
    this.animalCell = 100
    this.plantCell = 100

    // Fixed grid positions
    this.animalGridX = 0
    this.animalGridY = 0

    this.plantGridX =
      this.animalGridX + this.animalGridSize.cols * this.animalCell + 12
    this.plantGridY = 0

    // Fixed spawn position in world space (on the sidewalk)
    this.spawnX =
      this.plantGridX +
      this.plantGridSize.cols * this.plantCell +
      CONSTANTS.LAWN_BORDER +
      CONSTANTS.SIDEWALK_WIDTH

    this._redrawGround()

    // Adjust camera zoom for mobile devices
    this._adjustCameraZoom()

    // Handle window resize to recalculate camera zoom
    this.scale.on('resize', (gameSize) => {
      this._adjustCameraZoom()
    })

    // physics groups
    this.enemies = this.physics.add.group()
    this.projectiles = this.physics.add.group()

    // particle manager for hit effects
    this.hitParticleManager = this.add.particles(0, 0, '_dot', {
      lifespan: { min: 200, max: 450 },
      speed: { min: 80, max: 160 },
      angle: { min: 0, max: 360 },
      quantity: 8,
      scale: { start: 0.4, end: 0 },
      emitting: false,
    })
    this.hitParticleManager.setDepth(10)

    // input for placing and panning
    this.input.on('pointerdown', (pointer) => {
      // ignore when inventory is visible
      if (this.ui.inventory && this.ui.inventory.style.display === 'block')
        return
      if (this.ui.shop && this.ui.shop.style.display === 'block') return

      // Record starting position for drag detection
      this.dragStartX = pointer.x
      this.dragStartY = pointer.y

      this.isDragging = false
    })

    this.input.on('pointermove', (pointer) => {
      // ignore when inventory is visible
      if (this.ui.inventory && this.ui.inventory.style.display === 'block')
        return
      if (this.ui.shop && this.ui.shop.style.display === 'block') return

      // Only pan if pointer is down
      if (!pointer.isDown) return

      const camera = this.cameras.main
      const dx = pointer.x - this.dragStartX
      const distance = Math.abs(dx)

      if (distance > this.dragThreshold) {
        this.isDragging = true
        camera.scrollX = camera.scrollX - dx / camera.zoom
        // reset so drag feels continuous
        this.dragStartX = pointer.x
      }
    })

    this.input.on('pointerup', (pointer) => {
      // ignore when inventory is visible
      if (this.ui.inventory && this.ui.inventory.style.display === 'block')
        return
      if (this.ui.shop && this.ui.shop.style.display === 'block') return

      // If we were dragging, don't process as a click
      if (this.isDragging) {
        this.isDragging = false
        return
      }

      // Convert pointer coordinates to world coordinates (accounting for camera scroll AND zoom)
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y)
      const worldX = worldPoint.x
      const worldY = worldPoint.y

      // place plant
      const ppos = this._gridPos(
        worldX,
        worldY,
        this.plantGridX,
        this.plantGridY,
        this.plantCell,
        this.plantGridSize
      )
      if (ppos) {
        this.placePlant(ppos.row, ppos.col)
        return
      }

      // place farm animal
      const apos = this._gridPos(
        worldX,
        worldY,
        this.animalGridX,
        this.animalGridY,
        this.animalCell,
        this.animalGridSize
      )
      if (apos) {
        // Buy slot
        const slot = apos.row * this.animalGridSize.cols + apos.col
        if (
          this.tutorialFinished &&
          slot === this.slots &&
          this.money >= getSlotPrice(slot)
        ) {
          this.setMoney(this.money - getSlotPrice(slot))
          this.slots += 1
          this._redrawGround()
          this._syncUI()
        }

        if (this.selectedInventoryIndex >= 0) {
          this.placeFarmAnimalFromInventory(
            this.selectedInventoryIndex,
            apos.row,
            apos.col
          )
          this.selectedInventoryIndex = -1
        } else {
          this.moveFarmAnimalToInventory(apos.row, apos.col)
        }
      }
    })

    // collisions
    this.physics.add.overlap(this.projectiles, this.enemies, (proj, enemy) => {
      enemy.hp -= proj.damage
      this._hitBurst(enemy.x, enemy.y, 0xffb74d)
      // this.events.emit('projectile-hit')
      proj.destroy()
      if (enemy.hp <= 0) {
        this._hitBurst(enemy.x, enemy.y, 0xff7043)

        if (this.inventory.length < this.inventoryMax) {
          const item = {
            type: enemy.type,
            weight: enemy.weight,
            value: enemy.value,
            mutation: enemy.mutation,
          }
          this.inventory.push(item)

          // Index track
          const indexKey = `${enemy.type}_${enemy.mutation || 'Normal'}`
          if (this.indexUnlock[indexKey] === undefined)
            this.indexUnlock[indexKey] = true
        }

        enemy.label.destroy()
        enemy.hpBar.destroy()
        enemy.hpText.destroy()
        enemy.destroy()
        this.refreshInventoryHTML() // keep inventory list fresh if open

        this.events.emit('enemy-died')

        if (this.tutorialFinished === false && this.tutorialStep === 5)
          this._tutGo(6)
      }
    })

    this._syncUI(true)

    // Try to load saved state AFTER layout/background exists
    this.loadGame()

    // Save when tab goes hidden or before unload
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.saveGame()
    })
    window.addEventListener('beforeunload', () => this.saveGame())

    // spawners
    this._spawnEnemy()
    this._scheduleEnemySpawn()
    this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => this._tickMoney(),
    })

    if (IS_POKI) {
      PokiSDK.init()
        .then(() => {
          console.log('Poki SDK successfully initialized')
          // fire your function to continue to game

          PokiSDK.gameLoadingFinished()
        })
        .catch(() => {
          console.log('Initialized, something went wrong, load you game anyway')
          // fire your function to continue to game
        })
    }

    if (IS_CRAZY) {
      window.CrazyGames.SDK.init()
        .then(() => {
          console.log('CrazyGames SDK successfully initialized')
        })
        .catch(() => {
          console.log('Initialized, something went wrong, load you game anyway')
        })
    }

    if (IS_POKI || IS_CRAZY) {
      // First click
      function onFirstClick(e) {
        document.removeEventListener('click', onFirstClick)
        document.removeEventListener('touchstart', onFirstClick)

        if (IS_POKI) PokiSDK.gameplayStart()
        if (IS_CRAZY) window.CrazyGames.SDK.game.gameplayStart()
      }

      document.addEventListener('click', onFirstClick, { passive: true })
      document.addEventListener('touchstart', onFirstClick, { passive: true })
    }

    // Sound events
    // create background music, looped, lower volume
    if (!this.music) {
      this.music = this.sound.add('bgm', {
        loop: true,
        volume: 0.4, // keep it under the SFX punch
      })
      this.music.play()
    }

    // OPTIONAL: pause/resume on window blur/focus
    // this.game.events.on(Phaser.Core.Events.BLUR, () => {
    //   if (this.music && this.music.isPlaying) this.music.pause()
    // })
    // this.game.events.on(Phaser.Core.Events.FOCUS, () => {
    //   if (this.music && this.music.isPaused) this.music.resume()
    // })

    this.events.on('projectile-fired', () => {
      this.sfx.play(this.sfx.blast())
    })

    this.events.on('projectile-hit', () => {
      this.sfx.play(this.sfx.hit())
    })

    this.events.on('enemy-died', () => {
      this.sfx.play(this.sfx.enemyDie())
    })

    this.events.on('plant-placed', () => {
      this.sfx.play(this.sfx.plantPlop())
    })

    this.events.on('menu-click', () => {
      this.sfx.play(this.sfx.menuClick())
    })
  }

  update(_, dtMs) {
    const dt = dtMs / 1000

    // Manage weapon sprite - create/destroy based on weapon state
    if (this.weapon) {
      if (!this.weaponSprite) {
        // Create weapon sprite
        this.weaponSprite = this.add
          .image(0, 0, 'bat')
          .setOrigin(0.5)
          .setDisplaySize(32, 32)
          .setDepth(10000)
        this.weaponSprite.setOrigin(0, 1) // bottom-left (x=0, y=1)
      }
      // Update weapon sprite position to follow cursor
      const pointer = this.input.activePointer
      this.weaponSprite.setPosition(pointer.worldX, pointer.worldY)
      // Hide default cursor
      this.input.setDefaultCursor('none')
    } else {
      // Destroy weapon sprite when weapon is deactivated
      if (this.weaponSprite) {
        this.weaponSprite.destroy()
        this.weaponSprite = null
      }
      // Show appropriate cursor
      if (this.shovel) {
        this.input.setDefaultCursor('url(/assets/shovel.png) 12 12, pointer')
      } else {
        this.input.setDefaultCursor('default')
      }
    }

    // OPTIMIZATION: Pre-calculate which rows have enemies in range
    // This avoids the O(Plants * Enemies) nested loop in the plant check below.
    const rowsWithEnemies = new Set()
    const lawnW = this.plantCell * (this.plantGridSize.cols + 2)
    const enemyLimitX = this.plantGridX + lawnW

    // enemies move & sync labels/bars
    this.enemies.children.iterate((e) => {
      if (!e) return
      e.x -= e.speed * dt * 60
      if (e.x < -50) {
        e.label.destroy()
        e.hpBar.destroy()
        e.hpText.destroy()
        e.destroy()
        return
      }

      // Track if this enemy is in valid range for shooting
      if (e.x < enemyLimitX) {
        rowsWithEnemies.add(e.row)
      }

      e._sync()
    })

    // projectiles
    this.projectiles.children.iterate((p) => {
      if (!p) return
      p.x += p.speed * dt * 60
      if (p.x > this.scale.width + 50) p.destroy()
    })

    // plants
    const now = this.time.now
    this.plants.forEach((plant) => {
      // OPTIMIZATION: Check the pre-calculated set (O(1)) instead of iterating enemies (O(N))
      const hasEnemy = rowsWithEnemies.has(plant.row)

      if (hasEnemy && now - plant.lastShot > this.shootInterval) {
        this._shootFrom(plant)
        plant.lastShot = now
        this.tweens.add({
          targets: plant.text,
          angle: { from: -4, to: 0 },
          duration: 120,
          ease: 'quad.out',
        })
      }
      plant.text.y = plant.baseY + Math.sin(now / 350 + plant.phase) * 2
    })

    // farm animals idle bob
    this.farmAnimals.forEach((a) => {
      a.text.y = a.baseY + Math.sin(this.time.now / 500 + a.phase) * 2
    })

    // Ad refresh timer
    if (this.adRefreshTimer > 0) this.adRefreshTimer -= dtMs
    this._updateAdButtons()
  }

  // ====== HTML UI bridge ======
  setUIBindings({
    moneyEl,
    buyBtn,
    shop,
    shopList,
    inventory,
    inventoryListEl,
    seedInventory,
    closeShop,
    inventoryBtn,
    indexListEl,
    indexTabs,
    shovelBtn,
    weaponBtn,
    sellPlantsBtn,
    volumeBtn,
    volumeHighIcon,
    volumeMuteIcon,
    rebirthModalBody,
  }) {
    this.ui.moneyEl = moneyEl
    this.ui.buyBtn = buyBtn
    this.ui.shop = shop
    this.ui.shopList = shopList
    this.ui.inventory = inventory
    this.ui.inventoryListEl = inventoryListEl
    this.ui.seedInventory = seedInventory
    this.ui.shopCloseBtn = closeShop
    this.ui.inventoryBtn = inventoryBtn
    this.ui.indexListEl = indexListEl
    this.ui.indexTabs = indexTabs
    this.ui.shovelBtn = shovelBtn
    this.ui.weaponBtn = weaponBtn
    this.ui.sellPlantsBtn = sellPlantsBtn
    this.ui.volumeBtn = volumeBtn
    this.ui.volumeHighIcon = volumeHighIcon
    this.ui.volumeMuteIcon = volumeMuteIcon
    volumeBtn.addEventListener('click', () => {
      const isMuted = localStorage.getItem('muted') === '1'
      this.muteAudio(!isMuted, false)
      localStorage.setItem('muted', isMuted ? '0' : '1')
    })
    this.ui.rebirthModalBody = rebirthModalBody

    this._syncUI(true)
    this.refreshSeedInventory()
    this.muteAudio(false, true)

    if (!this.tutorialFinished) this._initTutorial()
  }

  _syncUI(skipSave = false) {
    if (this.ui.moneyEl)
      this.ui.moneyEl.textContent = humanizeNumber(this.money)
    if (this.ui.buyBtn) this.ui.buyBtn.disabled = this.money < 10

    if (!skipSave) this._scheduleSave()
  }

  equipBest() {
    // const capacity = this.animalGridSize.rows * this.animalGridSize.cols
    const capacity = this.slots

    // 1) Move current farm animals back to inventory and clean up visuals
    if (this.farmAnimals?.length) {
      this.farmAnimals.forEach((a) => {
        this.inventory.push({ type: a.type, weight: a.weight, value: a.value })
        // Destroy display objects
        if (a.text && a.text.destroy) a.text.destroy()
      })
      this.farmAnimals = []
    }

    if (!this.inventory || this.inventory.length === 0) {
      // Nothing to equip; UI refresh for consistency
      this.refreshInventoryHTML?.()
      this._syncUI?.()
      return
    }

    // 2) Normalize & sort ALL candidates by value (desc)
    this.inventory.forEach((animal) => {
      if (typeof animal.value !== 'number') {
        // Fallback heuristic: derive from value or weight
        const mv = Number(animal.value) || 0
        const wt = Number(animal.weight) || 0
        animal.value = Math.max(
          0,
          Math.round(mv / 5) || Math.round(wt / 10) || 0
        )
      }
    })

    // Sort a copy so we can reorder inventory cleanly
    const sorted = [...this.inventory].sort(
      (a, b) => (b.value || 0) - (a.value || 0)
    )

    // 3) Rebuild inventory in sorted order so we can place by stable indices
    this.inventory = sorted

    // 4) Place top-K animals into the farm grid (row-major) by consuming from inventory
    const toPlace = Math.min(capacity, this.inventory.length)
    let placed = 0
    for (let r = 0; r < this.animalGridSize.rows && placed < toPlace; r++) {
      for (let c = 0; c < this.animalGridSize.cols && placed < toPlace; c++) {
        // Always place the current best at index 0 (since we keep consuming from the front)
        this.placeFarmAnimalFromInventory(0, r, c)
        placed += 1
      }
    }
  }

  setMoney(amount) {
    this.money = amount
    this.topMoney = Math.max(this.topMoney, this.money)
  }

  sellAllPlants() {
    if (this.selectedSeed >= 0) {
      const p = this.seeds[this.selectedSeed]
      this.seeds.splice(this.selectedSeed, 1)
      this.setMoney(
        this.money +
          Math.round(p.attackPower ? p.attackPower * 10 : p.price * 0.8)
      )
      this.selectedSeed = -1
    } else {
      for (let i = this.seeds.length - 1; i >= 0; i--) {
        const p = this.seeds[i]
        this.setMoney(
          this.money +
            Math.round(p.attackPower ? p.attackPower * 10 : p.price * 0.8)
        )
        this.seeds.splice(i, 1)
      }
    }
    this.refreshSeedInventory()
  }

  sellAnimal(index) {
    const soldAnimal = this.inventory.splice(index, 1)[0]
    this.setMoney(this.money + Math.round((soldAnimal?.weight || 0) * 10))
  }

  sellAll() {
    this.inventory.forEach((animal) => {
      this.setMoney(this.money + Math.round((animal?.weight || 0) * 10))
    })
    this.inventory = []
    this.selectedInventoryIndex = -1
    this.refreshInventoryHTML()
    this._syncUI()
  }

  refreshInventoryHTML() {
    const list = this.ui.inventoryListEl
    if (!list) return

    // If inventory panel is hidden, don't rebuild; just mark dirty
    if (!this.ui.inventory || this.ui.inventory.style.display === 'none') {
      return
    }

    // Clear existing
    list.innerHTML = ''

    // Empty inventory message
    if (this.inventory.length === 0) {
      const msg = document.createElement('p')
      msg.style.gridColumn = '1/-1'
      msg.style.textAlign = 'center'
      msg.style.color = '#666'
      msg.textContent = 'No animals in inventory'
      list.appendChild(msg)
      return
    }

    // Loop through each animal
    this.inventory.forEach((animal, index) => {
      const type = this.animalTypes[animal.type]

      // Create container
      const div = document.createElement('div')
      div.className = 'animal-item'

      // --- Image ---
      const img = document.createElement('img')
      img.src = `./assets/animals/${animal.type}.png`
      img.alt = animal.type
      img.style.width = '64px'
      img.style.height = '64px'
      img.style.objectFit = 'contain'
      img.style.display = 'block'
      img.style.margin = '0 auto 6px'

      // --- Weight ---
      const weight = document.createElement('div')
      weight.style.textAlign = 'center'
      weight.style.fontSize = '12px'
      weight.textContent = `${animal.weight.toFixed(1)} kg`

      // --- Mutation ---
      const mutation =
        animalMutations.find((m) => m.name === animal.mutation) ||
        animalMutations[0]
      if (mutation.name !== 'Normal') {
        const mutationDiv = document.createElement('div')
        mutationDiv.style.textAlign = 'center'
        mutationDiv.style.fontSize = '11px'
        mutationDiv.style.fontWeight = 'bold'
        mutationDiv.style.color = mutation.color
        mutationDiv.textContent = mutation.name
        div.appendChild(mutationDiv)
      }

      // --- Value ---
      const value = document.createElement('div')
      value.style.textAlign = 'center'
      value.style.fontSize = '12px'
      value.style.marginBottom = '8px'
      value.textContent = `$${animal.value.toFixed(0)}/s`

      // --- Button ---
      const btn = document.createElement('button')
      btn.textContent = 'Select'
      btn.addEventListener('click', () => {
        this.selectedInventoryIndex = index
        if (this.ui.inventory) this.ui.inventory.style.display = 'none'
      })

      const sellBtn = document.createElement('button')
      sellBtn.textContent = 'Sell'
      sellBtn.addEventListener('click', () => {
        if (this.tutorialFinished === false) return
        this.sellAnimal(index)
        this.refreshInventoryHTML()
        this._syncUI()
      })

      // Assemble
      div.appendChild(img)
      div.appendChild(weight)
      div.appendChild(value)
      div.appendChild(btn)
      div.appendChild(sellBtn)
      list.appendChild(div)

      // Keep refs if needed
      animal._els = { placeBtn: btn }
    })
  }

  refreshShop() {
    const list = this.ui.shopList
    if (!list) return
    list.innerHTML = ''

    this.shop.forEach((seed, index) => {
      // Root container
      const div = document.createElement('div')
      div.className = 'shop-item'

      // Icon
      const iconDiv = document.createElement('div')
      iconDiv.style.fontSize = '64px'
      iconDiv.textContent = seed.icon

      // Details container
      const detailsDiv = document.createElement('div')
      detailsDiv.className = 'shop-item-details'

      // Name + rarity
      const nameDiv = document.createElement('div')
      nameDiv.className = 'shop-item-name'
      nameDiv.textContent = seed.name

      const rarityDiv = document.createElement('div')
      rarityDiv.className = 'shop-item-rarity ' + seed.rarity
      rarityDiv.textContent = seed.rarity

      // Stock info
      const stockDiv = document.createElement('div')
      stockDiv.textContent = '1x in stock'

      // Actions container
      const actionsDiv = document.createElement('div')
      actionsDiv.className = 'shop-item-actions'

      // Buy button
      const buyBtn = document.createElement('button')
      buyBtn.textContent = `$${seed.price.toLocaleString()}`
      buyBtn.addEventListener('click', () => {
        this.buySeed(seed)
        if (this.ui.shop) this.ui.shop.style.display = 'none'
        if (!this.tutorialFinished && this.tutorialStep === 2) this._tutGo(3)
        if (this.tutorialFinished === false && this.tutorialStep === 4)
          this._tutGo(5)
      })
      buyBtn.disabled = this.money < seed.price
      actionsDiv.appendChild(buyBtn)

      const adBtn = document.createElement('button')
      if (this.topMoney > seed.price && this.tutorialFinished) {
        // Ad button
        adBtn.className = 'ad-btn'
        adBtn.textContent = '🎬 Ad'
        adBtn.disabled = this.adRefreshTimer > 0
        adBtn.addEventListener('click', () => {
          if (this.adRefreshTimer <= 0) this.adRefreshTimer = AD_REFRESH
          // Handle ad click
          // console.log(`Watched ad for ${seed.name}`)

          this.muteAudio(true, false)

          if (IS_POKI)
            PokiSDK.rewardedBreak(() => {
              this.togglePause()
            }).then((success) => {
              if (success) {
                this.buySeed(seed, true)
                // if (this.ui.shop) this.ui.shop.style.display = 'none'
              } else {
              }
              console.log('Rewarded break finished, proceeding to game')
              this.togglePause()
              this.muteAudio(false, true)
            })

          if (IS_CRAZY) {
            window.CrazyGames.SDK.ad.requestAd('rewarded', {
              adFinished: () => {
                console.log('SEED BOUGHT')
                this.buySeed(seed, true)
                this.muteAudio(false, true)
              },
              adError: (error) => {
                console.log('Error midgame ad', error)
                this.muteAudio(false, true)
              },
              adStarted: () => console.log('Start midgame ad'),
            })
          }
        })
        actionsDiv.appendChild(adBtn)
      }

      // Assemble
      detailsDiv.appendChild(nameDiv)
      detailsDiv.appendChild(rarityDiv)
      // detailsDiv.appendChild(stockDiv)
      detailsDiv.appendChild(actionsDiv)

      div.appendChild(iconDiv)
      div.appendChild(detailsDiv)

      list.appendChild(div)

      // Keep refs if needed
      seed._els = { div, iconDiv, nameDiv, stockDiv, buyBtn, adBtn }
    })
  }

  refreshIndexList() {
    const list = this.ui.indexListEl
    if (!list) return

    // Clear existing
    list.innerHTML = ''

    const currMutation = this.ui.indexMutation || 'Normal'

    if (this.ui.indexTab === 1) {
      const mutationInfo =
        mutations.find((m) => m.name === currMutation) || mutations[0]

      this.shop.forEach((seed) => {
        const unlocked = this.indexUnlock[`${seed.name}_${currMutation}`]

        // Create container
        const div = document.createElement('div')
        div.className = 'animal-item'

        // --- Image ---
        const imgContainer = document.createElement('div')
        imgContainer.className = 'tinted'
        imgContainer.style.display = 'block'
        imgContainer.style.margin = '0 auto 6px'
        imgContainer.style.width = '64px'
        const img = document.createElement('div')
        img.innerHTML = seed.icon
        img.style.margin = '0 auto 6px'
        img.classList.add('animal-img')
        if (!unlocked) img.classList.add('locked')

        if (unlocked && mutationInfo.color) {
          // --- Tint ---
          const tint = document.createElement('tint')
          tint.textContent = seed.icon
          tint.classList.add('tint')
          tint.style.backgroundColor = mutationInfo.color || ''
          tint.classList.add('animal-img')
          tint.style.backgroundClip = 'text'
          tint.style.webkitBackgroundClip = 'text'
          tint.style.color = 'transparent'
          imgContainer.appendChild(tint)
        }
        imgContainer.appendChild(img)

        // --- Name ---
        const title = document.createElement('div')
        title.style.textAlign = 'center'
        title.textContent = unlocked ? seed.name : '???'
        title.className = 'animal-name'

        // --- Rarity ---
        const rarity = document.createElement('div')
        rarity.style.textAlign = 'center'
        rarity.textContent = seed.rarity
        rarity.className = 'animal-rarity ' + seed.rarity.toLowerCase()

        // Assemble
        div.appendChild(imgContainer)
        div.appendChild(title)
        div.appendChild(rarity)
        list.appendChild(div)

        // Keep refs if needed
        // animal._els = { placeBtn: btn }
      })
    } else {
      const mutationInfo =
        animalMutations.find((m) => m.name === currMutation) ||
        animalMutations[0]

      // Loop through all animals
      Object.keys(this.animalTypes)
        .sort((a, b) => {
          const rarityDiff =
            rarityRank[this.animalTypes[a].rarity] -
            rarityRank[this.animalTypes[b].rarity]
          if (rarityDiff !== 0) return rarityDiff
          return a.localeCompare(b)
        })
        .forEach((name) => {
          const info = this.animalTypes[name]
          const unlocked = this.indexUnlock[`${name}_${currMutation}`]

          // Create container
          const div = document.createElement('div')
          div.className = 'animal-item'

          // --- Image ---
          const imgContainer = document.createElement('div')
          imgContainer.className = 'tinted'
          imgContainer.style.display = 'block'
          imgContainer.style.margin = '0 auto 6px'
          imgContainer.style.width = '64px'
          const img = document.createElement('img')
          img.src = `./assets/animals/${name}.png`
          img.alt = name
          img.style.width = '64px'
          img.style.height = '64px'
          img.style.objectFit = 'contain'
          if (!unlocked) img.className = 'locked'
          imgContainer.appendChild(img)

          if (unlocked && mutationInfo.color) {
            // --- Tint ---
            const tint = document.createElement('tint')
            tint.classList.add('tint')
            tint.style.backgroundColor = mutationInfo.color || ''
            tint.style.maskImage = `url(./assets/animals/${encodeURIComponent(
              name
            )}.png)`
            tint.style.maskSize = 'contain'
            tint.style.maskRepeat = 'no-repeat'
            tint.style.maskPosition = 'center'
            tint.style.height = '64px'
            tint.style.width = '64px'
            imgContainer.appendChild(tint)
          }

          // --- Name ---
          const title = document.createElement('div')
          title.style.textAlign = 'center'
          title.textContent = unlocked ? name : '???'
          title.className = 'animal-name'

          // --- Rarity ---
          const rarity = document.createElement('div')
          rarity.style.textAlign = 'center'
          rarity.textContent = info.rarity
          rarity.className = 'animal-rarity ' + info.rarity.toLowerCase()

          // Assemble
          div.appendChild(imgContainer)
          div.appendChild(title)
          div.appendChild(rarity)
          list.appendChild(div)

          // Keep refs if needed
          // animal._els = { placeBtn: btn }
        })
    }
  }

  refreshIndex() {
    const tabs = this.ui.indexTabs
    if (!tabs) return
    tabs.innerHTML = ''
    const rarityTabs = document.createElement('div')
    rarityTabs.className = 'index-tabs'
    let mutationsList = mutations
    if (this.ui.indexTab === 1) mutationsList = animalMutations
    mutationsList.forEach((mutation, tabIndex) => {
      const tabBtn = document.createElement('button')
      tabBtn.textContent = mutation.name
      tabBtn.style.backgroundColor = mutation.color || ''
      if (
        this.ui.indexMutation === mutation.name ||
        (tabIndex === 0 && !this.ui.indexMutation)
      )
        tabBtn.classList.add('selected')
      else tabBtn.classList.remove('selected')
      tabBtn.addEventListener('click', () => {
        this.ui.indexMutation = mutation.name
        this.refreshIndex()
      })
      rarityTabs.appendChild(tabBtn)
    })
    tabs.appendChild(rarityTabs)
    this.refreshIndexList()
  }

  hasAnimal(type) {
    return (
      this.farmAnimals.some((a) => a.type === type) ||
      this.inventory.some((a) => a.type === type)
    )
  }

  refreshRebirth() {
    const content = this.ui.rebirthModalBody
    if (!content) return
    content.innerHTML = ''

    const rebirth = allRebirths[this.rebirths]

    const title = document.createElement('h1')
    title.textContent = `Rebirth ${this.rebirths + 1}`
    title.style.color = '#e644f0'
    content.appendChild(title)

    const subtitle = document.createElement('h3')
    subtitle.textContent = 'Requirements'
    content.appendChild(subtitle)

    const money = document.createElement('h2')
    money.textContent = '$' + rebirth.money.toLocaleString()
    money.style.color = '#0cf367'
    content.appendChild(money)

    const rebirthAnimals = document.createElement('div')
    rebirthAnimals.classList.add('rebirth-animals')
    rebirth.animals.forEach((req) => {
      const reqDiv = document.createElement('div')
      reqDiv.className = 'rebirth-animal-req'

      const img = document.createElement('img')
      img.src = `./assets/animals/${req}.png`
      img.alt = req
      img.style.width = '64px'
      img.style.height = '64px'
      img.style.objectFit = 'contain'
      reqDiv.appendChild(img)

      const qty = document.createElement('div')
      qty.textContent = req
      reqDiv.appendChild(qty)

      if (this.hasAnimal(req)) {
        const completed = document.createElement('img')
        completed.src = `./assets/check.png`
        completed.className = 'rebirth-animal-completed'
        reqDiv.appendChild(completed)
      }

      rebirthAnimals.appendChild(reqDiv)
    })
    content.appendChild(rebirthAnimals)

    const rewardsTitle = document.createElement('h3')
    rewardsTitle.textContent = 'You Unlock'
    content.appendChild(rewardsTitle)

    const rewards = document.createElement('div')
    rewards.classList.add('rebirth-animals')
    rewards.style.marginBottom = '16px'
    rebirth.rewards.forEach((req) => {
      const reqDiv = document.createElement('div')
      reqDiv.className = 'rebirth-animal-req'

      const img = document.createElement(req.icon ? 'img' : 'div')
      if (req.icon) {
        img.src = `./assets/${req.icon}.png`
        img.alt = req.description
        img.style.objectFit = 'contain'
      }
      img.style.width = '48px'
      img.style.height = '48px'
      reqDiv.appendChild(img)

      const qty = document.createElement('div')
      qty.textContent = req.description
      reqDiv.appendChild(qty)

      rewards.appendChild(reqDiv)
    })
    content.appendChild(rewards)

    const rebirthConfirm = document.createElement('button')
    rebirthConfirm.textContent = 'Rebirth'
    if (!this.canRebirth()) rebirthConfirm.disabled = true
    rebirthConfirm.addEventListener('click', () => {
      const modal = document.getElementById('rebirthConfirm')
      if (modal) modal.style.display = 'block'
    })

    content.appendChild(rebirthConfirm)
  }

  // Helper: mm:ss
  _formatMs(ms) {
    ms = Math.max(0, ms | 0)
    const s = Math.ceil(ms / 1000)
    const m = Math.floor(s / 60)
    const ss = String(s % 60).padStart(2, '0')
    return `${m}:${ss}`
  }

  // Call this in update() (lightweight) or on a 200ms timed event
  _updateAdButtons() {
    const left = Math.max(0, this.adRefreshTimer | 0)
    // Optimization: Only touch DOM when the second changes
    const seconds = Math.floor(left / 1000)
    if (this._lastAdSecond === seconds) return
    this._lastAdSecond = seconds

    const txt = left > 0 ? `🎬 ${this._formatMs(left)}` : '🎬 Ad'
    const disabled = left > 0

    // Touch only existing DOM nodes we saved
    this.shop?.forEach((seed) => {
      const btn = seed._els?.adBtn
      if (!btn) return
      if (btn.disabled !== disabled) btn.disabled = disabled
      // Avoid thrashing the layout: only change if needed
      if (btn.textContent !== txt) btn.textContent = txt
    })
  }

  refreshSeedInventory() {
    const inv = this.ui.seedInventory
    if (!inv) return
    inv.innerHTML = ''

    this.ui.sellPlantsBtn.style.display =
      this.seeds.length > 0 ? 'inline-block' : 'none'

    this.seeds.slice(0, 5).forEach((seed, index) => {
      const tooltip = document.createElement('div')
      tooltip.className = 'tooltip'

      const btn = document.createElement('button')
      btn.className = 'seed-btn'
      btn.textContent = seed.icon
      btn.title = `${seed.name}\nDMG: ${seed.damage}\nCooldown: ${seed.cooldown}s`
      btn.addEventListener('click', () => {
        // Select this seed for placement
        if (this.selectedSeed === index) this.selectedSeed = -1
        else this.selectedSeed = index
        this.refreshSeedInventory()
      })
      if (index === this.selectedSeed) btn.classList.add('selected')

      const tooltipText = document.createElement('span')
      tooltipText.className = 'tooltip-text'
      tooltipText.textContent = seed.name
      tooltip.appendChild(tooltipText)

      tooltip.appendChild(btn)
      inv.appendChild(tooltip)
    })
  }

  // ====== UI actions (called by HTML) ======
  buySeed(seed, free = false) {
    if (!free && this.money < seed.price) return
    if (!free) this.setMoney(this.money - seed.price)
    this.seeds.push(seed)
    this._syncUI()
    this.refreshShop()
    this.refreshSeedInventory()
  }

  _getLawnWidth(isInitial) {
    return (
      // Animal grid
      this.animalCell * (isInitial ? 2 : this.animalGridSize.cols) +
      CONSTANTS.LAWN_BORDER * 2 +
      // Plant grid
      this.plantCell * this.plantGridSize.cols +
      CONSTANTS.LAWN_BORDER * 2 +
      // Sidewalk
      CONSTANTS.SIDEWALK_WIDTH +
      CONSTANTS.LAWN_BORDER * 2
    )
  }

  // ====== World helpers ======
  _buildLawn() {
    // Layers in separate Graphics for easy redraw if resized
    if (this.bgGfx) this.bgGfx.destroy()
    this.bgGfx = this.add.graphics().setDepth(0)

    // Colors
    const farm = 0xeeb94d // brown
    const slot = 0x4b55da // dark blue
    // const buyColor = 0x0cf367 // bright green
    const dirt = 0x8d6e63 // brown
    const lawn = 0x6fbf73 // mid green
    const lawnDark = 0x5aaa62 // darker stripe
    const sidewalk = 0x6b6ec9 // light blue
    const sidewalkBorder = 0x010f88

    // Regions (based on precomputed cell sizes/positions)
    const dirtX = this.animalGridX - CONSTANTS.LAWN_BORDER
    const dirtY = this.animalGridY - CONSTANTS.LAWN_BORDER
    const dirtW =
      this.animalCell * this.animalGridSize.cols + CONSTANTS.LAWN_BORDER * 2
    const dirtH =
      this.animalCell * this.animalGridSize.rows + CONSTANTS.LAWN_BORDER * 2

    this.plantGridX =
      this.animalGridX +
      this.animalGridSize.cols * this.animalCell +
      CONSTANTS.LAWN_BORDER * 2
    const lawnX = this.plantGridX - CONSTANTS.LAWN_BORDER
    const lawnY = this.plantGridY - CONSTANTS.LAWN_BORDER
    const lawnW =
      this.plantCell * this.plantGridSize.cols + CONSTANTS.LAWN_BORDER * 2
    const lawnH =
      this.plantCell * this.plantGridSize.rows + CONSTANTS.LAWN_BORDER * 2
    // Fixed sidewalk width in world space
    const sidewalkW = CONSTANTS.SIDEWALK_WIDTH
    const sidewalkX =
      this.plantGridX +
      this.plantCell * this.plantGridSize.cols +
      CONSTANTS.LAWN_BORDER
    const sidewalkY = lawnY
    const sidewalkH = Math.max(lawnH, dirtH)

    // Dirt patch (farm grid area)
    this.bgGfx.fillStyle(farm, 1).fillRoundedRect(dirtX, dirtY, dirtW, dirtH, 8)

    // Lawn base (plant grid area)
    this.bgGfx.fillStyle(lawn, 1).fillRoundedRect(lawnX, lawnY, lawnW, lawnH, 8)

    // Lawn stripes per row
    for (let r = 0; r < this.plantGridSize.rows; r++) {
      const y = this.plantGridY + r * this.plantCell
      const stripeH = this.plantCell // full row height — solid stripe look

      const laneLocked = !this.lanes.includes(r)
      const color = laneLocked ? dirt : r % 2 === 0 ? lawnDark : lawn

      this.bgGfx
        .fillStyle(color, 1)
        .fillRect(
          this.plantGridX,
          y,
          this.plantCell * this.plantGridSize.cols,
          stripeH
        )

      if (!this.rowLabels) this.rowLabels = []
      if (this.rowLabels[r]) this.rowLabels[r].destroy()
      if (laneLocked) {
        // NEW: dmg label under the plant
        const dmgLabel = this.add
          .text(
            this.plantGridX + this.plantCell / 2,
            y + this.plantCell / 2 - 14,
            `$${humanizeNumber(this.getRowUnlockPrice(r))}`,
            {
              font: 'bold 20px Arial',
              color: '#0cf367',
            }
          )
          .setOrigin(0.5, 0)
          .setDepth(5)
        // Improve readability
        dmgLabel.setStroke('#000000', 3).setShadow(0, 1, '#00000055', 0)
        this.rowLabels[r] = dmgLabel
      }
    }

    // Farm locked slots
    if (this.slotText) this.slotText.destroy()

    const slotPadding = CONSTANTS.LAWN_BORDER * 2
    for (let i = 0; i < this.slots + 1; i++) {
      const r = Math.floor(i / this.animalGridSize.cols)
      const c = i % this.animalGridSize.cols
      const x = this.animalGridX + c * this.animalCell
      const y = this.animalGridY + r * this.animalCell

      if (i < this.slots) {
        this.bgGfx
          .fillStyle(slot, 1)
          .fillRoundedRect(
            x + slotPadding / 2,
            y + slotPadding / 2,
            this.animalCell - slotPadding,
            this.animalCell - slotPadding,
            8
          )
      } else if (i < this.animalGridSize.rows * this.animalGridSize.cols) {
        // const padding = 28
        // this.bgGfx
        //   .fillStyle(buyColor, 1)
        //   .fillRect(
        //     x + padding / 2,
        //     y + padding / 2,
        //     this.animalCell - padding,
        //     this.animalCell - padding
        //   )

        this.slotText = this.add
          .text(
            x + this.plantCell / 2,
            y + this.plantCell / 2 - 14,
            `$${humanizeNumber(getSlotPrice(i))}`,
            {
              font: 'bold 20px Arial',
              color: '#0cf367',
            }
          )
          .setOrigin(0.5, 0)
          .setDepth(5)
        this.slotText.setStroke('#000000', 3).setShadow(0, 1, '#00000055', 0)
      }
    }

    // Sidewalk on the far right (spawn side)
    const sidewalkPadding = CONSTANTS.LAWN_BORDER * 2
    this.bgGfx
      .fillStyle(sidewalkBorder, 1)
      .fillRect(sidewalkX, sidewalkY, sidewalkW, sidewalkH)
    this.bgGfx
      .fillStyle(sidewalk, 1)
      .fillRect(
        sidewalkX,
        sidewalkY + sidewalkPadding,
        sidewalkW - sidewalkPadding,
        sidewalkH - sidewalkPadding * 2
      )
    // Sidewalk lines
    // this.bgGfx
    //   .lineStyle(2, sidewalkBorder, 1)
    //   .strokeRect(sidewalkX, sidewalkY, sidewalkW, sidewalkH)
    for (let i = 0; i < 3; i++) {
      const y = sidewalkY + sidewalkH / 2
      this.bgGfx
        .fillStyle(0xffffff, 1)
        .fillRect(sidewalkX + i * 128 + 32, y, 64, 16)
    }
  }

  // Thin outlines for the two grids (optional – subtle)
  _drawGroundGrids() {
    const line = 0x263238 // dark slate
    if (this.gridGfx) this.gridGfx.destroy()
    this.gridGfx = this.add.graphics().setDepth(1)

    // Plant grid
    this.gridGfx.lineStyle(1, line, 0.35)
    for (let r = 0; r < this.plantGridSize.rows; r++) {
      for (let c = 0; c < this.plantGridSize.cols; c++) {
        const x = this.plantGridX + c * this.plantCell
        const y = this.plantGridY + r * this.plantCell
        this.gridGfx.strokeRect(x, y, this.plantCell, this.plantCell)
      }
    }

    // Farm (dirt) grid
    for (let r = 0; r < this.animalGridSize.rows; r++) {
      for (let c = 0; c < this.animalGridSize.cols; c++) {
        const x = this.animalGridX + c * this.animalCell
        const y = this.animalGridY + r * this.animalCell
        this.gridGfx.strokeRect(x, y, this.animalCell, this.animalCell)
      }
    }
  }

  // Helpers: if you ever change rows/cols dynamically, call this to rebuild
  _redrawGround() {
    this._buildLawn()
    this._drawGroundGrids()
    this._scheduleSave()
  }

  _gridPos(x, y, gridX, gridY, cell, size) {
    const col = Math.floor((x - gridX) / cell)
    const row = Math.floor((y - gridY) / cell)
    if (col >= 0 && col < size.cols && row >= 0 && row < size.rows)
      return { row, col }
    return null
  }

  getMultiplier() {
    // 1 = common, 5 = very rare
    // Skew distribution so 1 is most common
    const r = Math.random()

    let value
    if (r < 0.5) {
      // 50% chance
      value = 1
    } else if (r < 0.75) {
      // 25% chance
      value = 2
    } else if (r < 0.9) {
      // 15% chance
      value = 3
    } else if (r < 0.98) {
      // 8% chance
      value = 4
    } else {
      // 2% chance
      value = 5
    }

    // Add slight variation so it's not always an integer
    const jitter = Math.random() * 0.9 - 0.45 // between -0.45 and +0.45
    const rarity = Math.min(5, Math.max(1, value + jitter))

    return Number(rarity.toFixed(1))
  }

  rollMutation() {
    if (this.tutorialFinished === false) {
      return mutations[0] // Normal during tutorial
    }
    // Roll for a mutation based on weighted probabilities
    // Higher luck increases chances of rarer (lower chance) mutations
    // but in a controlled way (no insane 100x jumps).

    // 1) Compute min/max chance to derive a 0..1 rarity factor
    const chances = mutations.map((m) => m.chance)
    const minChance = Math.min(...chances)
    const maxChance = Math.max(...chances)
    const range = Math.max(maxChance - minChance, 1e-9) // avoid divide-by-zero

    // 2) Build adjusted weights
    const adjustedMutations = mutations.map((mutation) => {
      // Rarer = smaller chance → higher rarityFactor (0..1)
      // most common => 0, rarest => ~1
      const rarityFactor = (maxChance - mutation.chance) / range

      // luck = 1   => multiplier = 1 for everyone
      // luck = 1.5 => rarest ≈ 1.5x, common ≈ 1x, others in between
      const luckMultiplier = 1 + (this.luck - 1) * rarityFactor

      return {
        ...mutation,
        adjustedChance: mutation.chance * luckMultiplier,
      }
    })

    // 3) Weighted random pick using the adjusted chances
    const totalWeight = adjustedMutations.reduce(
      (sum, m) => sum + m.adjustedChance,
      0
    )

    const roll = Math.random() * totalWeight
    let cumulative = 0

    for (const mutation of adjustedMutations) {
      cumulative += mutation.adjustedChance
      if (roll < cumulative) {
        return mutation
      }
    }

    // Fallback to first mutation if something goes weird
    return mutations[0]
  }

  rollAnimalMutation() {
    const base = animalMutations

    // Find min/max chance so we can compute a 0..1 rarity factor
    const minChance = Math.min(...base.map((m) => m.chance))
    const maxChance = Math.max(...base.map((m) => m.chance))
    const range = Math.max(maxChance - minChance, 1e-9) // avoid div by zero

    const adjusted = base.map((mutation) => {
      // Rarer = smaller chance → higher rarityFactor (0..1)
      const rarityFactor = (maxChance - mutation.chance) / range
      // rarest ≈ 1, most common = 0

      // luck = 1   => multiplier = 1 for everyone
      // luck = 1.5 => rarest ≈ 1.5x, common ≈ 1x, others in between
      const luckMultiplier = 1 + (this.luck - 1) * rarityFactor

      return {
        ...mutation,
        weight: mutation.chance * luckMultiplier,
      }
    })

    const totalWeight = adjusted.reduce((sum, m) => sum + m.weight, 0)
    let roll = Math.random() * totalWeight

    for (const m of adjusted) {
      if (roll < m.weight) return m
      roll -= m.weight
    }

    return base[0] // fallback
  }

  getRowUnlockPrice(row) {
    if (row === 1 || row === 3) return 100000
    if (row === 0 || row === 4) return 2500000
    return 0
  }

  placePlant(row, col) {
    // Check for row unlock
    if (!this.lanes.includes(row)) {
      const price = this.getRowUnlockPrice(row)
      if (col === 0 && this.money >= price) {
        // Unlock the row
        this.lanes.push(row)
        this.setMoney(this.money - price)
        this._redrawGround()
        this._syncUI()
        this.refreshSeedInventory()
      }

      return
    }

    // Remove plants
    if (this.shovel) {
      const idx = this.plants.findIndex((p) => p.row === row && p.col === col)
      if (idx > -1) {
        const p = this.plants.splice(idx, 1)[0]
        this.seeds.push({
          icon: p.icon,
          attackPower: p.attackPower,
          mutation: p.mutation,
        }) // refund seed
        p.text?.destroy()
        p.dmgLabel?.destroy()
        this.shovel = false
        this.ui.shovelBtn.classList.toggle('selected')
        this._syncUI()
        this.refreshSeedInventory()
      }

      return
    }

    if (this.seeds.length <= 0) return
    if (this.plants.some((p) => p.row === row && p.col === col)) return

    const seed =
      this.selectedSeed > -1
        ? this.seeds.splice(this.selectedSeed, 1)[0]
        : this.seeds.pop()

    const x = this.plantGridX + col * this.plantCell + this.plantCell / 2
    const y = this.plantGridY + row * this.plantCell + this.plantCell / 2

    // Apply mutation only for new plants (not picked up ones)
    let multiplier = 1
    let mutation = null
    let mutationInfo = null

    if (seed.attackPower) {
      // Plant was picked up - keep original multiplier and mutation
      mutation = seed.mutation || null
      mutationInfo = mutations.find((m) => m.name === mutation) || mutations[0]
    } else {
      // New plant - roll for mutation
      mutationInfo = this.rollMutation()
      mutation = mutationInfo.name
      const baseMultiplier = this.getMultiplier()
      multiplier = baseMultiplier * mutationInfo.multiplier

      const indexKey = `${seed.name}_${mutation || 'Normal'}`
      if (this.indexUnlock[indexKey] === undefined)
        this.indexUnlock[indexKey] = true
    }

    const attackPower = seed.attackPower || Math.round(multiplier * seed.damage)

    // Main icon
    const text = this.add
      .text(x, y, seed.icon, {
        font: `${this.unitSize}px Arial`,
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(5)
    text.setStroke('#000', 3)
    if (mutationInfo.color) text.setTint(hexToPhaser(mutationInfo.color))

    // Damage label with mutation color
    const labelColor = mutationInfo?.color || 'black'
    const labelText =
      mutationInfo && mutationInfo.name !== 'Normal'
        ? `${mutationInfo.name} DMG ${humanizeNumber(attackPower)}`
        : `DMG ${humanizeNumber(attackPower)}`

    const dmgLabel = this.add
      .text(x, y + this.plantCell / 2 - 14, labelText, {
        font: 'bold 12px Arial',
        color: labelColor,
      })
      .setOrigin(0.5, 0)
      .setDepth(5)
    // Improve readability
    dmgLabel.setStroke('#ffffff', 3).setShadow(0, 1, '#00000055', 0)

    const plant = {
      row,
      col,
      icon: seed.icon,
      text,
      dmgLabel,
      baseY: y,
      phase: Math.random() * Math.PI * 2,
      lastShot: 0,
      attackPower, // store value used in label
      mutation, // store mutation name for saving/loading
    }

    this.plants.push(plant)
    this._syncUI()
    this.refreshSeedInventory()
    this.selectedSeed = -1 // reset selection after placement
    if (this.tutorialFinished === false && this.tutorialStep === 4)
      this._tutGo(5)

    this.events.emit('plant-placed')
  }

  moveFarmAnimalToInventory(row, col) {
    const currentAnimalIndex = this.farmAnimals.findIndex(
      (animal) => animal.row === row && animal.col === col
    )
    if (currentAnimalIndex !== -1) {
      const currentAnimal = this.farmAnimals[currentAnimalIndex]
      // Add current animal to inventory and remove from farmAnimals
      this.inventory.push({
        type: currentAnimal.type,
        weight: currentAnimal.weight,
        value: currentAnimal.value,
      })
      // Destroy display objects
      currentAnimal.text.destroy()
      this.farmAnimals.splice(currentAnimalIndex, 1)
    }
  }

  placeFarmAnimalFromInventory(index, row, col) {
    if (index < 0 || index >= this.inventory.length) return
    if (row * this.animalGridSize.cols + col >= this.slots) return

    const a = this.inventory.splice(index, 1)[0]
    const x = this.animalGridX + col * this.animalCell + this.animalCell / 2
    const y = this.animalGridY + row * this.animalCell + this.animalCell / 2
    const t = this.add
      .image(x, y, `animal_${a.type}`)
      .setOrigin(0.5)
      .setDisplaySize(this.unitSize * 1.5, this.unitSize * 1.5)
      .setDepth(1)
    const mutation =
      animalMutations.find((m) => m.name === a.mutation) || animalMutations[0]
    if (mutation.color) t.setTint(hexToPhaser(mutation.color))

    this.moveFarmAnimalToInventory(row, col)

    this.farmAnimals.push({
      row,
      col,
      text: t,
      baseY: y,
      type: a.type,
      weight: a.weight,
      value: a.value,
      phase: Math.random() * Math.PI * 2,
      mutation: a.mutation,
    })

    // reflect removal in HTML inventory automatically
    this.refreshInventoryHTML()

    if (this.tutorialStep === 8) this._tutGo(9)
    this.events.emit('plant-placed')
  }

  _scheduleEnemySpawn() {
    // Clear any existing timer before starting a new one
    if (this._enemySpawnTimer) {
      this._enemySpawnTimer.remove(false)
      this._enemySpawnTimer = null
    }

    // const delay = Math.floor(this.spawnMax / this.lanes.length)
    const timeModifier = Math.max(this.plants.length / 3, 1)
    const delay = Math.max(Math.floor(this.spawnMax / timeModifier), 2000)

    // Schedule the next spawn
    this._enemySpawnTimer = this.time.delayedCall(delay, () => {
      this._spawnEnemy()
      this._scheduleEnemySpawn() // recursively schedule again
    })
  }

  // Heavily bias to Rare/Epic early so players can win.
  // Early-mode when you have <= 2 slots (tweak as you like).
  getRandomEnemyType() {
    const byRarity = {}
    for (const [name, data] of Object.entries(this.animalTypes)) {
      const r = data.rarity || 'Rare'
      ;(byRarity[r] ||= []).push({ name, ...data })
    }

    const isEarly = (this.slots || 1) <= 2

    if (isEarly) {
      // Target rarity shares in early game (very generous with Rares)
      // Adjust these numbers to taste.
      const targetShare = {
        Rare: 0.8,
        Epic: 0.18,
        Legendary: 0.015,
        Godly: 0.004,
        Mythic: 0.0008,
        Limited: 0.0006,
        Secret: 0.0006,
      }

      // Keep only rarities that actually exist
      const available = Object.keys(targetShare).filter(
        (r) => byRarity[r]?.length
      )

      // Renormalize if some tiers are missing
      const sum = available.reduce((s, r) => s + targetShare[r], 0)
      const norm = available.reduce((obj, r) => {
        obj[r] = targetShare[r] / (sum || 1)
        return obj
      }, {})

      // Pick a rarity bucket
      let roll = Math.random()
      let pickedR = available[0]
      for (const r of available) {
        roll -= norm[r]
        if (roll <= 0) {
          pickedR = r
          break
        }
      }

      // Pick a random animal within that rarity
      return Phaser.Utils.Array.GetRandom(byRarity[pickedR])
    }

    // ===== Normal mode (post-early): luck-biased weighted pick =====
    // Higher luck = rarer tiers more likely
    const baseWeights = {
      Rare: 120,
      Epic: 70,
      Legendary: 8,
      Godly: 3,
      Mythic: 1.2,
      Limited: 1,
      Secret: 0.3,
    }

    // Build per-animal weights with luck boost
    // Rarer tiers (lower base weight) get boosted more by luck
    const weights = []
    let total = 0

    for (const [rarity, list] of Object.entries(byRarity)) {
      if (!list?.length) continue
      const baseWeight = baseWeights[rarity] ?? 1

      // Boost factor: rarer enemies (lower base weight) benefit more from luck
      const rarityBoost = baseWeight < 10 ? 10 / baseWeight : 1
      const luckMultiplier = 1 + (this.luck - 1) * rarityBoost
      const rarityWeight = baseWeight * luckMultiplier

      const perAnimal = rarityWeight / list.length
      for (const entry of list) {
        weights.push([entry, perAnimal])
        total += perAnimal
      }
    }

    let roll = Math.random() * (total || 1)
    for (const [entry, w] of weights) {
      roll -= w
      if (roll <= 0) return entry
    }

    // Fallback
    const anyRarity = Object.keys(byRarity).find((r) => byRarity[r]?.length)
    return Phaser.Utils.Array.GetRandom(byRarity[anyRarity] || [])
  }

  _getAnimalValue(animal, weight) {
    // --- CONFIG ---
    const anchorHealth = 100 // Noobini Pizzanini's health
    const anchorEarnings = 5 // Noobini earns $5/sec baseline
    const exponent = 0.6 // scaling curve (health influence)

    // Rarity multipliers — controls how valuable higher tiers are
    const rarityScale = {
      Rare: 1.0,
      Epic: 1.8,
      Legendary: 2.8,
      Godly: 4.0,
      Mythic: 5.5,
      Limited: 6.0,
      Secret: 8.0,
    }

    // --- COMPUTATION ---
    const rarityMult = rarityScale[animal.rarity] ?? 1.0
    const healthRatio = Math.max(1, animal.health / anchorHealth)

    // Base = $5 * (health scaling) * (rarity scaling)
    const base = anchorEarnings * Math.pow(healthRatio, exponent) * rarityMult

    return Math.round(base * weight)
  }

  _spawnEnemy(animalName) {
    if (this.plants.length === 0) return

    const row = this.lanes[Phaser.Math.Between(0, this.lanes.length - 1)]
    const y = this.plantGridY + row * this.plantCell + this.plantCell / 2
    const x = this.spawnX

    const enemy = this.physics.add.sprite(x, y).setCircle(15).setOrigin(0.5)
    enemy.displayWidth = 30
    enemy.displayHeight = 30
    enemy.row = row
    enemy.speed = 0.5
    enemy.type = animalName || this.getRandomEnemyType().name

    const type = this.animalTypes[enemy.type]
    enemy.weight = Phaser.Math.FloatBetween(1, 2)

    // Roll for animal mutation
    const animalMutation = this.rollAnimalMutation()
    enemy.mutation = animalMutation.name

    // Apply mutation multiplier to value
    const baseValue = this._getAnimalValue(type, enemy.weight)
    enemy.value = Math.round(baseValue * (animalMutation.multiplier || 1))

    enemy.hp = type.health * enemy.weight * (animalMutation.multiplier || 1)
    enemy.maxHp = enemy.hp
    enemy.label = this.add
      .image(x, y, `animal_${enemy.type}`)
      .setOrigin(0.5)
      .setDisplaySize(this.unitSize * 1.5, this.unitSize * 1.5)
      .setDepth(1)
      .setFlipX(true)
    if (animalMutation.color)
      enemy.label.setTint(hexToPhaser(animalMutation.color))

    enemy.hpBar = this.add.graphics().setDepth(2)
    enemy.hpText = this.add
      .text(x, y, '', {
        font: 'bold 11px Arial',
        color: '#ffffff',
      })
      .setOrigin(0.5, 0.5) // center inside the bar
      .setDepth(3)
      .setStroke('#000', 3) // outline for readability
      .setShadow(0, 1, '#00000055', 0)

    this.tweens.add({
      targets: [enemy, enemy.label],
      y: y + 3,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    })

    enemy._sync = () => {
      enemy.label.setPosition(enemy.x, enemy.y)

      // Move the HP bar graphics to match the enemy position
      enemy.hpBar.setPosition(enemy.x, enemy.y)

      // OPTIMIZATION: Only redraw graphics if HP actually changed
      if (
        enemy._lastHpForBar !== enemy.hp ||
        enemy._lastMaxHpForBar !== enemy.maxHp
      ) {
        enemy.hpBar.clear()

        const pct = Phaser.Math.Clamp(enemy.hp / enemy.maxHp, 0, 1)

        // Draw relative to (0,0) since we move the Graphics object with setPosition
        const hpBarY = -this.unitSize / 2 - 24
        const hpBarWidth = 64
        const hpBarHeight = 16

        // HP bar outline
        enemy.hpBar
          .fillStyle(0x000000, 0.3)
          .fillRect(
            -(hpBarWidth / 2 + 2),
            hpBarY,
            hpBarWidth + 4,
            hpBarHeight + 2
          )
        enemy.hpBar
          .fillStyle(0xf44336)
          .fillRect(-hpBarWidth / 2, hpBarY + 1, hpBarWidth, hpBarHeight)
        enemy.hpBar
          .fillStyle(0x4caf50)
          .fillRect(-hpBarWidth / 2, hpBarY + 1, hpBarWidth * pct, hpBarHeight)
        enemy.hpBar
          .lineStyle(1, 0x000000)
          .strokeRect(-hpBarWidth / 2, hpBarY + 1, hpBarWidth, hpBarHeight)

        // Update the cached values
        enemy._lastHpForBar = enemy.hp
        enemy._lastMaxHpForBar = enemy.maxHp

        // Update text position if needed (it moves with the bar)
        if (enemy.hpText) {
          // Text is a separate object, so it needs absolute positioning
          const absoluteBarY = enemy.y + hpBarY
          enemy.hpText.setPosition(enemy.x, absoluteBarY + 1 + hpBarHeight / 2)
        }
      } else {
        // If no redraw, just update text position to follow enemy
        if (enemy.hpText) {
          const hpBarY = -this.unitSize / 2 - 24
          const absoluteBarY = enemy.y + hpBarY
          const hpBarHeight = 16
          enemy.hpText.setPosition(enemy.x, absoluteBarY + 1 + hpBarHeight / 2)
        }
      }

      // Update text content only if needed
      if (
        enemy.hpText &&
        (enemy._lastHp !== enemy.hp || enemy._lastMaxHp !== enemy.maxHp)
      ) {
        const cur = Math.max(0, Math.ceil(enemy.hp)).toLocaleString()
        const max = humanizeNumber(Math.max(1, Math.ceil(enemy.maxHp)))
        enemy.hpText.setText(`${cur}/${max}`)
        enemy._lastHp = enemy.hp
        enemy._lastMaxHp = enemy.maxHp
      }
    }

    // Force initial draw
    enemy._sync()

    // Make enemy clickable for damage
    enemy.label.setInteractive({ useHandCursor: true })
    enemy.label.on('pointerdown', () => {
      if (this.isPaused || !this.weapon || !this.weaponSprite) return

      // Swing animation on the persistent weapon sprite
      this.tweens.add({
        targets: this.weaponSprite,
        rotation: -1,
        duration: 150,
        ease: 'quad.out',
        onComplete: () => {
          // Reset rotation after swing
          this.weaponSprite.setRotation(0)
        },
      })

      // Deal damage
      enemy.hp -= this.weaponDamage
      enemy._sync()

      // Show floating damage number with random direction
      const offsetX = Phaser.Math.Between(-15, 15)
      const offsetY = Phaser.Math.Between(-20, -10)

      const damageText = this.add
        .text(enemy.x + offsetX, enemy.y + offsetY, `-${this.weaponDamage}`, {
          font: 'bold 16px Arial',
          color: '#ff4444',
        })
        .setOrigin(0.5)
        .setDepth(10)
        .setStroke('#000', 3)
        .setShadow(0, 1, '#00000055', 0)

      // Animate the damage text floating up and fading
      this.tweens.add({
        targets: damageText,
        y: damageText.y - 30,
        x: damageText.x + Phaser.Math.Between(-10, 10), // Add horizontal drift
        alpha: 0,
        duration: 800,
        ease: 'quad.out',
        onComplete: () => damageText.destroy(),
      })

      // Check if enemy died from click
      if (enemy.hp <= 0) {
        this._hitBurst(enemy.x, enemy.y, 0xff7043)

        if (this.inventory.length < this.inventoryMax) {
          const item = {
            type: enemy.type,
            weight: enemy.weight,
            value: enemy.value,
            mutation: enemy.mutation,
          }
          this.inventory.push(item)

          // Index track
          const indexKey = `${enemy.type}_${enemy.mutation || 'Normal'}`
          if (this.indexUnlock[indexKey] === undefined)
            this.indexUnlock[indexKey] = true
        }

        enemy.label.destroy()
        enemy.hpBar.destroy()
        enemy.hpText.destroy()
        enemy.destroy()
        this.refreshInventoryHTML()
      }
    })

    this.enemies.add(enemy)
  }

  _hasEnemyInRow(row) {
    let found = false
    const lawnW = this.plantCell * (this.plantGridSize.cols + 2)
    this.enemies.children.iterate((e) => {
      if (e && e.row === row && e.x < this.plantGridX + lawnW) found = true
    })
    return found
  }

  _shootFrom(plant) {
    const p = this.physics.add
      .image(plant.text.x + this.plantCell / 2 - 10, plant.text.y, 'proj')
      .setOrigin(0.5)
    p.setCircle(6)
    p.displayWidth = 12
    p.displayHeight = 12
    p.speed = 3
    p.damage = plant.attackPower

    this.tweens.add({ targets: p, angle: 360, duration: 600, repeat: -1 })
    this._hitBurst(p.x, p.y, 0xffd54f)
    this.projectiles.add(p)
  }

  _tickMoney() {
    let total = 0
    this.farmAnimals.forEach((a) => {
      const increase = a.value * (this.moneyMultiplier ?? 1)
      total += increase
      const pop = this.add
        .text(a.text.x, a.text.y - 26, `+$${increase.toFixed(0)}`, {
          font: 'bold 12px Arial',
          color: '#00E676',
        })
        .setOrigin(0.5)
      pop.setDepth(1)
      this.tweens.add({
        targets: pop,
        y: pop.y - 18,
        alpha: 0,
        duration: 700,
        ease: 'quad.out',
        onComplete: () => pop.destroy(),
      })
    })
    this.setMoney(this.money + total)
    this._syncUI()
  }

  _adjustCameraZoom() {
    // Use initial width for zoom calculation (not current expanded width)
    const requiredWidth = this._getLawnWidth(true)

    const requiredHeight =
      this.animalGridSize.rows * this.animalCell + CONSTANTS.LAWN_BORDER * 2

    // Get the actual viewport dimensions
    const viewportWidth = this.scale.width
    const viewportHeight = this.scale.height

    // Calculate zoom needed to fit initial content
    const zoomX = viewportWidth / requiredWidth
    const zoomY = viewportHeight / (requiredHeight + CONSTANTS.HUD_HEIGHT)

    // Use the smaller zoom to ensure everything fits
    const zoom = Math.min(zoomY, zoomX, 1) // Don't zoom in beyond 1x

    const camera = this.cameras.main

    const currentLawnWidth = this._getLawnWidth()
    const visibleWidth = camera.worldView.width

    let cameraStartX = currentLawnWidth / 2 - visibleWidth / 2

    if (visibleWidth < requiredWidth) cameraStartX = -CONSTANTS.SCROLL_PADDING

    camera.setBounds(
      cameraStartX,
      Number.NEGATIVE_INFINITY,
      requiredWidth + CONSTANTS.SCROLL_PADDING,
      Number.POSITIVE_INFINITY
    )

    // Apply the zoom
    camera.setZoom(zoom)

    camera.centerOn(
      currentLawnWidth / 2,
      requiredHeight / 2 - CONSTANTS.HUD_HEIGHT / 2
    )

    console.log(
      `Camera zoom set to ${zoom.toFixed(
        2
      )}x (viewport: ${viewportWidth}x${viewportHeight}, current: ${currentLawnWidth})`
    )
  }

  _hitBurst(x, y, color = 0xffd54f) {
    if (this.hitParticleManager) {
      this.hitParticleManager.emitParticleAt(x, y, 8)
      // Note: Tinting a shared emitter overrides previous particles in some Phaser versions.
      // For simple "white dot" sparks, keeping them white or one color is fastest.
      // If we need color per burst, we'd need multiple managers or a more complex setup.
      // For now, let's just emit. If we badly need color, we can setTint on the manager,
      // but it might tint existing particles.
      // this.hitParticleManager.setTint(color)
    }
  }

  pauseGame() {
    if (this.isPaused) return
    this.isPaused = true
    this.scene.pause() // pauses this scene completely
  }

  resumeGame() {
    if (!this.isPaused) return
    this.isPaused = false
    this.scene.resume() // resumes this scene
  }

  togglePause() {
    this.isPaused ? this.resumeGame() : this.pauseGame()
  }

  // ========== TUTORIAL ==========
  _initTutorial() {
    // step index (1..9); 0 = off
    this.tutorialStep = 1

    // Overlay DOM elements (reused)
    this._tut = {
      mask: null,
      callout: null,
      hiRect: null,
      moShop: null, // mutation observers
      moInv: null,
    }

    // Create (hidden) mask & callout
    this._tut.mask = document.getElementById('tut-mask')
    this._tut.mask.style.display = 'none'

    this._tut.callout = document.getElementById('tut-callout')
    this._tut.callout.style.display = 'none'

    // Phaser highlight rectangle for on-canvas cues
    if (this._tut.hiRect) this._tut.hiRect.destroy()
    this._tut.hiRect = this.add.graphics().setDepth(10000)

    // Watch panel open/close (if DOM nodes exist)
    this._observePanelVisibility()

    // Kick off step 1
    this._tutGo(1)
  }

  _tutText(step) {
    return (
      [
        null,
        'Open the seed shop',
        'Buy your first seed',
        'Close the shop',
        'Plant your seed',
        'Wait for the plant to defeat a brainrot',
        'Open your inventory',
        'Select your brainrot',
        'Place your brainrot in the farm to make you money',
        'Tutorial complete! 🎉 Beat brainrots, make money, and buy bigger plants!',
      ][step] || ''
    )
  }

  _tutGo(step) {
    this.tutorialStep = step
    this._tutClearHighlights()

    const ui = this.ui
    const say = this._tutText(step)

    // Default: show mask + callout
    this._tut.mask.style.display = step === 0 ? 'none' : 'block'
    this._tut.callout.style.display = step === 0 ? 'none' : 'block'
    this._tut.callout.textContent = say

    const placeCalloutNear = (elOrRect) => {
      const r = elOrRect?.getBoundingClientRect
        ? elOrRect.getBoundingClientRect()
        : elOrRect
      const callout = this._tut.callout
      if (!r || !callout) {
        // fallback corner
        callout.style.left = '20px'
        callout.style.top = '20px'
        return
      }

      // Center horizontally under the target
      const x = r.left + r.width / 2 - callout.offsetWidth / 2
      const y = r.bottom + 8 // 8px padding below the element

      callout.style.left = `${Math.max(12, x)}px`
      callout.style.top = `${Math.max(12, y)}px`
    }

    // Helper to glow a DOM element
    const glow = (el) => el && el.classList.add('tut-glow')
    const unglow = (el) => el && el.classList.remove('tut-glow')

    // Helper to highlight a rect on canvas
    const hiRect = (x, y, w, h) => {
      this._tut.hiRect.clear()
      this._tut.hiRect
        .lineStyle(4, 0xf62626, 1)
        .strokeRoundedRect(x, y, w, h, 10)
      this.tweens.add({
        targets: this._tut.hiRect,
        alpha: { from: 0.6, to: 1 },
        duration: 400,
        yoyo: true,
        repeat: -1,
      })
    }

    // Per-step highlight target
    switch (step) {
      case 1: // Open Seed Shop
        glow(ui.buyBtn)
        placeCalloutNear(ui.buyBtn)
        break

      case 2: // Buy first plant (first item’s buy button)
        // Try to highlight the first buy button if we stored refs
        const first = this.shop?.[0]?._els?.buyBtn
        glow(first)
        placeCalloutNear(first)
        break

      case 3: // Close shop
        if (ui.shop) ui.shop.style.display = 'none'
        // glow(ui.closeShop)
        // placeCalloutNear(ui.closeShop)
        break

      case 4: // Plant the seed -> highlight plant grid
        hiRect(
          this.plantGridX,
          this.plantGridY + this.plantCell * 2,
          this.plantCell,
          this.plantCell
        )
        this._tut.mask.style.display = 'none'
        // placeCalloutNear(this.canvas?.getBoundingClientRect?.())
        break

      case 5: // Wait for plant kill
        this._spawnEnemy('Noobini Pizzanini')
        this._scheduleEnemySpawn()
        // placeCalloutNear(this.canvas?.getBoundingClientRect?.())
        this._tut.mask.style.display = 'none'
        break

      case 6: // Click inventory
        this._tut.mask.style.display = 'block'
        glow(ui.inventoryBtn)
        placeCalloutNear(ui.inventoryBtn)
        break

      case 7: // Select animal
        const firstAnimal = this.inventory?.[0]?._els?.placeBtn
        glow(firstAnimal || ui.inventoryListEl)
        placeCalloutNear(firstAnimal || ui.inventoryListEl)
        break

      case 8: // Place animal in farm
        if (this._tut.mask) this._tut.mask.style.display = 'none'

        hiRect(
          this.animalGridX,
          this.animalGridY,
          this.animalCell,
          this.animalCell
        )
        // placeCalloutNear(this.canvas?.getBoundingClientRect?.())
        break

      case 9: // End
        this._tut.callout.textContent = this._tutText(9)
        setTimeout(() => this._tutEnd(), 1200)
        this.tutorialFinished = true
        break
    }

    // Keep callout visible when there’s no obvious element
    if (
      this._tut.callout.style.display !== 'none' &&
      !this._tut.callout.style.left
    ) {
      placeCalloutNear()
    }
  }

  _tutClearHighlights() {
    const ui = this.ui
    ;[
      ui.buyBtn,
      ui.shop,
      ui.inventoryBtn,
      ui.inventory,
      ui.inventoryListEl,
    ].forEach((el) => el && el.classList.remove('tut-glow'))
    this._tut.hiRect?.clear()
  }

  _tutEnd() {
    this.tutorialStep = 0
    this._tutClearHighlights()
    if (this._tut.mask) this._tut.mask.style.display = 'none'
    if (this._tut.callout) this._tut.callout.style.display = 'none'
  }

  _observePanelVisibility() {
    // Observe display changes for shop/inventory panels
    const mkObs = (el, onShow, onHide) => {
      if (!el) return null
      const obs = new MutationObserver(() => {
        const shown = el.style.display !== 'none'
        shown ? onShow?.() : onHide?.()
      })
      obs.observe(el, { attributes: true, attributeFilter: ['style'] })
      return obs
    }

    // Shop
    this._tut.moShop = mkObs(
      this.ui.shop,
      () => {
        if (this.tutorialStep === 1) this._tutGo(2)
      }, // opened -> step 2
      () => {
        if (this.tutorialStep === 3) this._tutGo(4)
      } // closed -> step 4
    )

    // Inventory
    this._tut.moInv = mkObs(
      this.ui.inventory,
      () => {
        if (this.tutorialStep === 6) this._tutGo(7)
      }, // opened -> step 7
      () => {
        if (this.tutorialStep === 7) this._tutGo(8)
      } // closed -> step 4
    )
  }

  muteAudio(muted, setFromSave) {
    let isMuted = muted
    // If is ad check if user muted previously
    if (setFromSave) isMuted = localStorage.getItem('muted') === '1'

    if (isMuted) this.sfx.ctx.suspend()
    else this.sfx.ctx.resume()
    this.music.mute = isMuted

    if (this.ui.volumeHighIcon && this.ui.volumeMuteIcon) {
      this.ui.volumeHighIcon.style.display = isMuted ? 'none' : 'inline'
      this.ui.volumeMuteIcon.style.display = isMuted ? 'inline' : 'none'
    }
  }

  canRebirth() {
    const rebirth = allRebirths[this.rebirths]
    // Check if player has met all requirements
    if (!rebirth) return false
    if (this.money < rebirth.money) return false
    let hasAllAnimals = true
    rebirth.animals.forEach((animal) => {
      if (!this.hasAnimal(animal)) hasAllAnimals = false
    })
    return hasAllAnimals
  }

  performRebirth() {
    if (!this.canRebirth()) return

    const rebirth = allRebirths[this.rebirths]

    // Rewards
    if (rebirth.luckMultiplier) this.luck *= rebirth.luckMultiplier
    if (rebirth.moneyMultiplier) this.moneyMultiplier *= rebirth.moneyMultiplier
    if (rebirth.farmCols) this.animalGridSize.cols = rebirth.farmCols
    if (rebirth.weaponDamage) this.weaponDamage += rebirth.weaponDamage

    this.money = 0
    this.inventory = []
    this.seeds = []
    this.slots = 1

    this._clearFarm()

    this.rebirths++

    this._redrawGround()
    this._refreshPlantPositions()
  }

  _refreshPlantPositions() {
    this.plants.forEach((p) => {
      const x = this.plantGridX + p.col * this.plantCell + this.plantCell / 2
      const y = this.plantGridY + p.row * this.plantCell + this.plantCell / 2

      p.text.setPosition(x, y)
      p.baseY = y // Update base Y for bobbing

      if (p.dmgLabel) {
        p.dmgLabel.setPosition(x, y + this.plantCell / 2 - 14)
      }
    })
  }
}
